import { useEffect, useState } from 'react';
import BasicModal from './BasicModal';
import InviteOthersForm from '../InviteOthersForm';
import { useDiffitUserContext } from '../../contexts/DiffitUserContext';
import { usePremiumOffer } from '../../contexts/premiumOffer';
import { Mixpanel } from '../../services/Mixpanel';
import { requestCurriculum } from '../../services/UserService';
import { getSchoolNameForUser } from '../../utils/users';
import { splitAddressInput } from '../../utils/inviteOthers';
import { isValidEmail } from '../../utils/email';

/**
 * Asks a teacher's admin to enable a locked curriculum in Diffit. The admin
 * replies to the email, and Customer Success enables it for the district.
 * A teacher who isn't paid also gets 30 days of Premium, once.
 */
export default function RequestCurriculumModal({ curriculum, onClose }) {
  const {
    diffitUser,
    selectedOrg,
    hasActiveSubscription,
    refreshDiffitUser,
    setShowEditProfileModal,
  } = useDiffitUserContext();
  // The endpoint names the school in the email, and resolves it in this same order.
  const hasSchool = Boolean(getSchoolNameForUser(selectedOrg, diffitUser));
  const { isOffered, hasRedeemed } = usePremiumOffer();
  // isOffered is false while the paygate-premium-offer flag is off, and the backend grants nothing then.
  const offersDays = !hasActiveSubscription && isOffered && !hasRedeemed;

  const [recipients, setRecipients] = useState({ invited: [], inviteDraft: '' });
  const [note, setNote] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(null);

  useEffect(() => {
    if (!curriculum) return;
    setRecipients({ invited: [], inviteDraft: '' });
    setNote('');
    setError(null);
    setSent(null);
  }, [curriculum]);

  // Fold the uncommitted draft in, so an address typed without pressing Enter still counts.
  const emails = Array.from(
    new Set([
      ...recipients.invited,
      ...splitAddressInput(recipients.inviteDraft).filter(isValidEmail),
    ])
  );

  const handleSubmit = async () => {
    setIsSubmitting(true);
    setError(null);
    try {
      const response = await requestCurriculum({
        emails,
        note,
        curriculumId: curriculum.id,
        curriculumName: curriculum.name,
      });
      const trialExtended = Boolean(response.data?.trial_extended);
      Mixpanel.track('Curriculum Access Requested', {
        curriculum_id: curriculum.id,
        curriculum_source: curriculum.source,
        recipient_count: response.data?.emails_sent ?? emails.length,
        has_note: Boolean(note),
        offered_days: offersDays,
        trial_extended: trialExtended,
      });
      // The grant moves the trial end date, so the user this client holds is stale.
      if (trialExtended) refreshDiffitUser?.();
      // Only the addresses that failed stay in the field, so a retry doesn't email the rest twice.
      const failed = (response.data?.errors || []).filter((e) => typeof e === 'string');
      if (failed.length) {
        setRecipients({ invited: failed, inviteDraft: '' });
        setError(`We couldn't reach: ${failed.join(', ')}. Everyone else has been emailed.`);
        return;
      }
      setSent({ trialExtended });
    } catch (err) {
      // The domain rules reject with a message naming what to fix.
      setError(
        err.response?.data?.user_error_message ||
          'There was an error emailing your admin. Please try again.'
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const name = curriculum?.displayName || curriculum?.name;

  // Closes this modal first, so the two don't stack.
  const openProfileEditor = () => {
    Mixpanel.track('Curriculum Request Profile Link Clicked', { curriculum_id: curriculum?.id });
    onClose();
    setShowEditProfileModal?.(true);
  };

  return (
    <BasicModal
      ariaLabel='Request your curriculum'
      showModal={Boolean(curriculum)}
      setShowModal={(show) => !show && onClose()}
      appear
      maxWidth='sm:max-w-lg'
      dialogPadding='p-6 sm:p-8'>
      {sent ? (
        <>
          <h3 className='text-xl font-semibold text-gray-900'>Your request is on its way!</h3>
          <p className='mt-3 text-sm text-gray-600'>
            We emailed your admin. When they reply, we'll turn on {name} for your school.
          </p>
          {sent.trialExtended && (
            <p className='mt-3 text-sm text-gray-600'>
              In the meantime, we've added 30 days of premium access to your account.
            </p>
          )}
          <button
            type='button'
            onClick={onClose}
            className='mt-6 w-full rounded-md bg-emerald-700 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-800 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2'>
            Done
          </button>
        </>
      ) : (
        <>
          <h3 className='text-xl font-semibold text-gray-900'>Align to {name}</h3>
          <p className='mt-3 text-sm text-gray-600'>
            {offersDays
              ? `Ask your admin to turn on ${name} for your school. In the meantime, we'll add 30 days of premium access to your account.`
              : `Ask your admin to turn on ${name} for your school.`}
          </p>

          <div className='mt-5'>
            <InviteOthersForm
              selections={recipients}
              setSelections={setRecipients}
              size='compact'
              autoFocus={false}
              isSubmitting={isSubmitting}
              placeholder="Enter your admin's email address"
            />
          </div>

          <div className='mt-4'>
            <label
              htmlFor='request-curriculum-note'
              className='block text-sm font-semibold text-gray-900'>
              Add a note <span className='font-normal text-gray-500'>(optional)</span>
            </label>
            <textarea
              id='request-curriculum-note'
              rows={3}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder='How would this help you and your students?'
              className='mt-2 block w-full rounded-md border-gray-300 shadow-sm text-sm text-gray-900 placeholder:text-gray-400 focus:border-emerald-500 focus:ring-emerald-500'
            />
          </div>

          {!hasSchool && (
            <p className='mt-4 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800'>
              To email your admin,{' '}
              <button
                type='button'
                onClick={openProfileEditor}
                className='font-medium text-emerald-700 underline hover:text-emerald-600'>
                update your profile
              </button>{' '}
              with your school or district name.
            </p>
          )}

          {error && (
            <p className='mt-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700'>
              <span>{error}</span>
            </p>
          )}

          <button
            type='button'
            onClick={handleSubmit}
            disabled={emails.length === 0 || isSubmitting || !hasSchool}
            className='mt-6 w-full rounded-md bg-emerald-700 px-5 py-3 text-sm font-semibold text-white hover:bg-emerald-800 disabled:opacity-50 disabled:hover:bg-emerald-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2'>
            {isSubmitting ? 'Sending…' : 'Ask My Admin to Turn It On'}
          </button>
          <p className='text-xs mt-1.5 text-gray-600 text-center'>
            We'll send an email with your request
          </p>
        </>
      )}
    </BasicModal>
  );
}
